/**
 * Shared Trip Calculation Utilities
 * Centralized calculations for trip nights, segments, and statistics
 * Used by Dashboard.js, TripDetails.js, and other components
 */

/**
 * Calculate total nights for a trip based on start and end dates
 * @param {Object} trip - Trip object with start_date and end_date
 * @returns {number} - Number of nights
 */
export const calculateTripNights = (trip) => {
  if (!trip || !trip.start_date || !trip.end_date) return 0;
  
  // Parse dates in local timezone to avoid timezone conversion bugs
  const [startYear, startMonth, startDay] = trip.start_date.split('-').map(Number);
  const [endYear, endMonth, endDay] = trip.end_date.split('-').map(Number);
  const startDate = new Date(startYear, startMonth - 1, startDay);
  const endDate = new Date(endYear, endMonth - 1, endDay);
  
  const diffTime = Math.abs(endDate - startDate);
  // CRITICAL FIX: Don't use Math.ceil for date-only calculations
  // This was adding an extra day due to potential rounding
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Calculate nights from a single segment (accommodation, cruise, or overnight flight)
 * @param {Object} segment - Segment object with start_datetime and end_datetime
 * @returns {number} - Number of nights for this segment
 */
export const calculateSegmentNights = (segment) => {
  if (!segment.start_datetime || !segment.end_datetime) return 0;
  
  if (segment.type === 'accommodation' || segment.type === 'cruise') {
    const startDate = new Date(segment.start_datetime);
    const endDate = new Date(segment.end_datetime);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    const diffTime = Math.abs(endDate - startDate);
    return Math.round(diffTime / (1000 * 60 * 60 * 24));
  } else if (segment.type === 'flight') {
    // Overnight flights count as nights
    const departureDate = new Date(segment.start_datetime);
    const arrivalDate = new Date(segment.end_datetime);
    const depDate = new Date(departureDate.getFullYear(), departureDate.getMonth(), departureDate.getDate());
    const arrDate = new Date(arrivalDate.getFullYear(), arrivalDate.getMonth(), arrivalDate.getDate());
    const diffTime = arrDate - depDate;
    return Math.max(0, diffTime / (1000 * 60 * 60 * 24));
  }
  return 0;
};

/**
 * Calculate planned nights from accommodation and flight segments
 * @param {Array} segments - Array of segment objects
 * @returns {number} - Total planned nights
 */
export const calculatePlannedNights = (segments) => {
  const accommodationSegments = segments.filter(s => 
    !s.is_cancelled && !s.is_mistake &&
    (s.type === 'accommodation' || s.type === 'cruise')
  );
  const flightSegments = segments.filter(s => 
    !s.is_cancelled && !s.is_mistake &&
    s.type === 'flight'
  );
  let totalNights = 0;
  
  // Count accommodation nights
  accommodationSegments.forEach(segment => {
    totalNights += calculateSegmentNights(segment);
  });
  
  // Count flight nights (overnight flights)
  flightSegments.forEach(segment => {
    totalNights += calculateSegmentNights(segment);
  });
  
  return totalNights;
};

/**
 * Calculate nights breakdown by status with real-time progress tracking
 * 
 * This enhanced version:
 * - Filters out cancelled/mistake segments
 * - Tracks real-time progress for active stays (splits completed vs remaining nights)
 * - Handles accommodation, cruise, and overnight flight segments
 * 
 * @param {Array} segments - Array of segment objects
 * @returns {Object} - Object with plannedNights, bookedNights, completedNights
 */
/**
 * Calculate nights breakdown by status (calendar-grid, dedup-safe).
 *
 * Past algorithm summed each segment's nights individually, which
 * double-counted any overlapping bookings (e.g. two hotels sharing a
 * check-out/check-in day) and produced `completed + booked > tripNights`.
 *
 * New algorithm walks the trip's date range night-by-night and assigns
 * each night exactly one status, so totals always sum to `tripNights`:
 *
 *   - completed: night-date is in the past (< today). Calendar reality —
 *     once a night has happened, it's spent regardless of what the
 *     segment's `board_list` says.
 *   - booked:    future night covered by an accommodation/cruise/overnight
 *     flight whose `board_list` is `booked` or `completed`.
 *   - planned:   future night covered by a segment whose `board_list` is
 *     `planned` (or missing → defaults to planned).
 *   - (implicit) pending/unbooked: future nights with no segment coverage —
 *     not returned from this function; caller derives via
 *     `tripNights - completed - booked - planned`.
 *
 * Status precedence within the future when multiple segments overlap a
 * given night: booked > planned. Active stay segments (started but not
 * ended) are treated as covering all their nights from today onward.
 *
 * @param {Array} segments - Array of segment objects
 * @param {Object} [trip]  - Optional trip with `start_date` + `end_date`.
 *   Required for accurate per-night dedup; if omitted, falls back to the
 *   legacy sum-per-segment behaviour (kept for callers that haven't been
 *   migrated yet).
 * @returns {Object} `{ plannedNights, bookedNights, completedNights }`
 */
export const calculateNightsByStatus = (segments, trip) => {
  // Legacy callers without a trip object — fall back to the old summing
  // behaviour so existing call sites don't regress until they're updated.
  if (!trip || !trip.start_date || !trip.end_date) {
    return _legacyCalculateNightsByStatus(segments);
  }

  // Build the trip's night grid: one entry per night, keyed by the
  // check-in date YYYY-MM-DD. A trip Mar 15 → May 21 has nights
  // ["2026-03-15", "2026-03-16", ..., "2026-05-20"] — 67 entries.
  const [sy, sm, sd] = trip.start_date.split("-").map(Number);
  const [ey, em, ed] = trip.end_date.split("-").map(Number);
  const tripStart = new Date(sy, sm - 1, sd);
  tripStart.setHours(0, 0, 0, 0);
  const tripEnd = new Date(ey, em - 1, ed);
  tripEnd.setHours(0, 0, 0, 0);
  const tripNights = Math.floor((tripEnd - tripStart) / 86400000);
  if (tripNights <= 0) {
    return { plannedNights: 0, bookedNights: 0, completedNights: 0 };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Status array, one slot per trip-night. 0=unbooked, 1=planned, 2=booked.
  // Past nights are handled separately as `completed` below.
  const status = new Array(tripNights).fill(0);

  const candidates = segments.filter(
    s => !s.is_cancelled && !s.is_mistake &&
         (s.type === "accommodation" || s.type === "cruise" || s.type === "flight")
  );

  for (const seg of candidates) {
    // Parse the segment's covered nights as `[startDateOnly, endDateOnly)`.
    // Date-only strings ("YYYY-MM-DD") are parsed as LOCAL midnight to
    // avoid the well-known timezone shift that `new Date("2026-03-15")`
    // (UTC) introduces in non-UTC timezones. Datetime strings with an
    // explicit time/zone are left to the Date constructor.
    const rawStart = seg.start_datetime || seg.start_date;
    const rawEnd = seg.end_datetime || seg.end_date || seg.arrival_date;
    if (!rawStart || !rawEnd) continue;

    const parseLocal = (raw) => {
      if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [y, m, d] = raw.split("-").map(Number);
        return new Date(y, m - 1, d);
      }
      return new Date(raw);
    };

    const segStart = parseLocal(rawStart);
    segStart.setHours(0, 0, 0, 0);
    const segEnd = parseLocal(rawEnd);
    segEnd.setHours(0, 0, 0, 0);
    if (segEnd <= segStart) continue;

    // Map segment dates to trip-grid indices, clipped to the grid.
    const firstIdx = Math.max(0, Math.floor((segStart - tripStart) / 86400000));
    const lastIdx = Math.min(tripNights, Math.floor((segEnd - tripStart) / 86400000));
    if (lastIdx <= firstIdx) continue;

    // Status precedence: booked > planned. `completed` board status is
    // treated the same as `booked` here (a completed booking implies a
    // night was reserved); past-date detection below will reclassify it
    // to `completed` automatically.
    const bl = seg.board_list || "planned";
    const segScore = bl === "booked" || bl === "completed" ? 2 : 1;

    for (let i = firstIdx; i < lastIdx; i++) {
      if (segScore > status[i]) status[i] = segScore;
    }
  }

  // Tally with past-date override.
  let plannedNights = 0;
  let bookedNights = 0;
  let completedNights = 0;
  for (let i = 0; i < tripNights; i++) {
    const nightDate = new Date(tripStart.getTime() + i * 86400000);
    if (nightDate < today) {
      completedNights++;
      continue;
    }
    if (status[i] === 2) bookedNights++;
    else if (status[i] === 1) plannedNights++;
    // status === 0 → unbooked: not returned, caller derives pending.
  }

  return { plannedNights, bookedNights, completedNights };
};

// ---------------------------------------------------------------------------
// Legacy sum-per-segment implementation. Kept for backward compatibility
// with callers that don't pass a `trip` object. Will be removed once every
// call site is migrated.
// ---------------------------------------------------------------------------
const _legacyCalculateNightsByStatus = (segments) => {
  const nightSegments = segments.filter(s =>
    !s.is_cancelled && !s.is_mistake &&
    (s.type === "accommodation" || s.type === "cruise" || s.type === "flight")
  );

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  let plannedNights = 0;
  let bookedNights = 0;
  let completedNights = 0;

  nightSegments.forEach(segment => {
    if (segment.type === "cruise" || segment.type === "accommodation") {
      const startDate = new Date(segment.start_datetime || segment.start_date);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(segment.end_datetime || segment.end_date);
      endDate.setHours(0, 0, 0, 0);

      const totalNights = calculateSegmentNights(segment);
      if (totalNights === 0) return;

      if (endDate < now) {
        completedNights += totalNights;
      } else if (startDate > now) {
        const s = segment.board_list || "planned";
        if (s === "planned") plannedNights += totalNights;
        else if (s === "booked") bookedNights += totalNights;
        else if (s === "completed") completedNights += totalNights;
      } else {
        const daysSinceStart = Math.floor((now - startDate) / 86400000);
        const completedSegmentNights = Math.min(daysSinceStart, totalNights);
        const remainingSegmentNights = totalNights - completedSegmentNights;
        completedNights += completedSegmentNights;
        const s = segment.board_list || "booked";
        if (s === "planned") plannedNights += remainingSegmentNights;
        else bookedNights += remainingSegmentNights;
      }
    } else {
      const nights = calculateSegmentNights(segment);
      if (nights === 0) return;
      const endDate = new Date(segment.end_datetime || segment.end_date || segment.arrival_date);
      endDate.setHours(0, 0, 0, 0);
      if (endDate < now) {
        completedNights += nights;
      } else {
        const s = segment.board_list || "planned";
        if (s === "planned") plannedNights += nights;
        else if (s === "booked") bookedNights += nights;
        else if (s === "completed") completedNights += nights;
      }
    }
  });

  return { plannedNights, bookedNights, completedNights };
};

/**
 * Calculate unique countries from segments
 * @param {Array} segments - Array of segment objects
 * @returns {Promise<number>} - Number of unique countries visited
 */
export const calculateCountries = async (segments) => {
  try {
    const { calculateCountryVisits, getTotalDaysByCountry } = await import('./visitCalculator');
    const visits = await calculateCountryVisits(segments);
    const totalsByCountry = getTotalDaysByCountry(visits);
    // Filter out "International Waters" from country count
    const actualCountries = totalsByCountry.filter(({ country }) => country !== "International Waters");
    return actualCountries.length;
  } catch (error) {
    // Fallback to simple calculation
    const segments_with_country = segments.filter(s => 
      !s.is_cancelled && !s.is_mistake &&
      (s.type === 'accommodation' || s.type === 'cruise') && s.country
    );
    const uniqueCountries = new Set(segments_with_country.map(s => s.country));
    return uniqueCountries.size;
  }
};

/**
 * Get list of unique country names from segments (for flag display)
 */
export const getCountryNames = async (segments) => {
  try {
    const { calculateCountryVisits, getTotalDaysByCountry } = await import('./visitCalculator');
    const visits = await calculateCountryVisits(segments);
    const totalsByCountry = getTotalDaysByCountry(visits);
    return totalsByCountry
      .filter(({ country }) => country !== "International Waters")
      .map(({ country }) => country);
  } catch {
    const segments_with_country = segments.filter(s =>
      !s.is_cancelled && !s.is_mistake &&
      (s.type === 'accommodation' || s.type === 'cruise') && s.country
    );
    return [...new Set(segments_with_country.map(s => s.country))];
  }
};

/**
 * Calculate total nights for all segments (simple sum, no status breakdown)
 * @param {Array} segments - Array of segment objects
 * @returns {number} - Total nights across all valid segments
 */
export const calculateTotalSegmentNights = (segments) => {
  return segments
    .filter(s => !s.is_cancelled && !s.is_mistake)
    .reduce((total, segment) => total + calculateSegmentNights(segment), 0);
};
